import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import InstallApp from "@/components/InstallApp";
import AdminBackup from "@/pages/admin/AdminBackup";

import Login from "@/pages/Login";
import Invoice from "@/pages/Invoice";
import CustomerLayout from "@/components/CustomerLayout";
import Home from "@/pages/customer/Home";
import Categories from "@/pages/customer/Categories";
import CategoryProducts from "@/pages/customer/CategoryProducts";
import Search from "@/pages/customer/Search";
import ProductDetail from "@/pages/customer/ProductDetail";
import Cart from "@/pages/customer/Cart";
import Orders from "@/pages/customer/Orders";
import OrderDetail from "@/pages/customer/OrderDetail";
import Account from "@/pages/customer/Account";

import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import QuickAdd from "@/pages/admin/QuickAdd";
import BulkPhotos from "@/pages/admin/BulkPhotos";
import TrayBarcodes from "@/pages/admin/TrayBarcodes";
import TrayScan from "@/pages/admin/TrayScan";
import AdminConcepts from "@/pages/admin/AdminConcepts";
import AdminSearchAnalytics from "@/pages/admin/AdminSearchAnalytics";
import AdminBulkGram from "@/pages/admin/AdminBulkGram";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian">
      <img src="/orse-logo.png" alt="ORSE GOLD" className="h-14 w-auto object-contain animate-pulse" />
    </div>
  );
}

function RequireAuth({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready || user === undefined) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/home" replace />;
  if (role === "customer" && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready || user === undefined) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/invoice/:id" element={<RequireAuth><Invoice /></RequireAuth>} />

              <Route element={<RequireAuth role="customer"><CustomerLayout /></RequireAuth>}>
                <Route path="/home" element={<Home />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/category/:name" element={<CategoryProducts />} />
                <Route path="/search" element={<Search />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/:id" element={<OrderDetail />} />
                <Route path="/account" element={<Account />} />
              </Route>

              <Route element={<RequireAuth role="admin"><AdminLayout /></RequireAuth>}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/customers" element={<AdminCustomers />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/group-6007" element={<AdminProducts fixedGroup="6007" />} />
                <Route path="/admin/backup" element={<AdminBackup />} />
                <Route path="/admin/quick-add" element={<QuickAdd />} />
                <Route path="/admin/bulk-photos" element={<BulkPhotos />} />
                <Route path="/admin/tray-barcodes" element={<TrayBarcodes />} />
                <Route path="/admin/tray-scan" element={<TrayScan />} />
                <Route path="/admin/concepts" element={<AdminConcepts />} />
                <Route path="/admin/search-analytics" element={<AdminSearchAnalytics />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/bulk-gram" element={<AdminBulkGram />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <InstallApp />
          <Toaster position="top-center" theme="dark" richColors />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
